import { flip, size } from '@floating-ui/react-dom'
import {
  Select as MuiSelect,
  type PopupMiddleware,
  type SelectProps,
  useFormControlContext,
} from '@mui/base'
import { type JSX } from 'react'

import { warnFormControlPropUsage } from '../FormControl/warnFormControlPropUsage'

import { SelectListbox } from './Listbox'
import { SelectPopup } from './Popup'
import { SelectRoot } from './Root'

const selectPopupSizeMiddleware: PopupMiddleware = size({
  apply: ({ elements, rects }) => {
    Object.assign(elements.floating.style, {
      width: `${rects.reference.width}px`,
    })
  },
})

const middleware: PopupMiddleware[] = [flip(), selectPopupSizeMiddleware]

export { type SelectProps } from '@mui/base'

/**
 * Select components are used for collecting user provided information from a list of options.
 */
export const Select = <
  OptionValue extends NonNullable<unknown>,
  Multiple extends boolean = false,
>(
  props: SelectProps<OptionValue, Multiple>,
): JSX.Element => {
  const {
    'aria-label': ariaLabel,
    disabled: disabledProp,
    required: requiredProp,
    slotProps,
    slots: slotsProp,
    ...other
  } = props

  const slots = {
    listbox: SelectListbox,
    popup: SelectPopup,
    root: SelectRoot,
    ...slotsProp,
  }

  const formControlContext = useFormControlContext()

  // inspired by https://github.com/mui/material-ui/blob/d16c76e41d1872e40499e1b6d1b4427f615997c8/packages/mui-base/src/useInput/useInput.ts#L38-L72
  /* eslint-disable @typescript-eslint/prefer-destructuring -- external code logic */
  let disabled: boolean | undefined
  let required: boolean | undefined

  if (formControlContext) {
    disabled = formControlContext.disabled
    required = formControlContext.required

    if (process.env.NODE_ENV !== 'production') {
      warnFormControlPropUsage({
        componentLabel: 'Select',
        formControlContext,
        props: props as Record<string, unknown>,
      })
    }
  } else {
    disabled = disabledProp
    required = requiredProp
  }
  /* eslint-enable @typescript-eslint/prefer-destructuring */

  return (
    <MuiSelect
      aria-label={ariaLabel}
      disabled={disabled}
      required={required}
      slotProps={{
        ...slotProps,
        listbox: {
          // Forward aria-label to the listbox so axe aria-input-field-name rule
          // finds an accessible name on the ul[role="listbox"] element.
          'aria-label': ariaLabel,
          ...slotProps?.listbox,
        },
        popup: {
          middleware,
          ...slotProps?.popup,
        },
      }}
      slots={slots}
      {...other}
    />
  )
}
